import { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import {
  catalogVolumeToBodymap,
  volumeBySlugToHeatmap,
  type CatalogMuscleSlug,
} from '@heraion/shared';

import { ScaleLegend, makeCardBoxStyle } from '../index';

import { BodyMap } from '../bodymap/BodyMap';
import { useTheme } from './theme';

// Live "Muscles worked" heatmap for the active-logging footer. Themed
// (theme.heatmapRamp — green→red volume) and volume-weighted from the DRAFT's
// per-muscle totals, so it updates as you log. Replaces the old un-themed
// primary/secondary WorkoutBodyMap (Antenore 2026-07-01: keep the map during
// the workout, but themed — the primary/secondary one was a no-go). Post-save,
// PostSessionSummaryCard shows the equivalent map from the server SessionResult
// (the two never co-render — WorkoutMetaFooter gates them mutually exclusive).
export function WorkoutHeatMap({
  byMuscle,
  profile,
}: {
  byMuscle: ReadonlyArray<{ muscle: CatalogMuscleSlug; volumeKg: number }>;
  profile: import('./hostDeps').LoggerProfile;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = makeStyles(theme);

  const bodyMapValue = useMemo(() => {
    const catalogVolume: Partial<Record<CatalogMuscleSlug, number>> = {};
    for (const { muscle, volumeKg } of byMuscle) {
      catalogVolume[muscle] = (catalogVolume[muscle] ?? 0) + volumeKg;
    }
    return volumeBySlugToHeatmap(
      catalogVolumeToBodymap(catalogVolume),
      theme.heatmapRamp,
    );
  }, [byMuscle, theme.heatmapRamp]);

  if (Object.keys(bodyMapValue).length === 0) return null;

  return (
    <View style={styles.card}>
      <Text style={styles.title}>
        {t('mobile.postSession.bodyTitle', { defaultValue: 'Muscles worked' })}
      </Text>
      <View style={styles.wrap}>
        <BodyMap
          mode="display"
          view="front"
          value={bodyMapValue}
          scale={1.2}
          figure={profile.preferredFigure ?? 'masculine'}
          layout="dual"
          theme={theme}
        />
      </View>
      <ScaleLegend variant="ramp" theme={theme} stops={theme.heatmapRamp} />
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, typography: ty } = theme;
  return StyleSheet.create({
    card: makeCardBoxStyle(theme),
    title: {
      color: p.ink2,
      fontSize: ty.sizes.xs,
      fontWeight: '700',
      letterSpacing: 0.6,
      textTransform: 'uppercase',
    },
    wrap: { alignItems: 'center' },
  });
}
